import { Box, Stack, Typography } from '@mui/material'

/**
 * The client-record section idiom at contact-panel scale: a hairline rule
 * between sections instead of nested cards, one heading level, and spacing
 * that comes from the wrapper rather than from each block's own margin. The
 * panel previously used ad-hoc dividers and per-block `mb: 2`, so section
 * boundaries landed in different places depending on which blocks happened to
 * be visible.
 *
 * A section that can be empty renders this itself rather than being wrapped by
 * its parent — the parent cannot know whether the child has anything to show,
 * and wrapping one that hides itself leaves a ruled band of blank space.
 *
 * `dense` halves the vertical rhythm, for a dialog rather than a page. A modal
 * is read in one go and its height is the user's scroll budget, so the spacing
 * that gives a full record room to breathe just pushes the submit button out of
 * sight.
 */
export default function PanelSection({
  title,
  aside,
  first,
  dense,
  children,
}: {
  title?: string
  aside?: React.ReactNode
  first?: boolean
  dense?: boolean
  children: React.ReactNode
}) {
  const pad = dense ? 2 : 3
  return (
    <Box
      component="section"
      sx={{
        pt: first ? 0 : pad,
        pb: dense && first ? 0 : pad,
        borderTop: first ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      {title && (
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', mb: dense ? 1 : 1.5 }}
        >
          <Typography variant="subtitle2" component="h3">
            {title}
          </Typography>
          {aside}
        </Stack>
      )}
      {children}
    </Box>
  )
}
